import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal, ModalHeader, ModalBody, Button } from 'reactstrap'

import { useDashboardRedux } from '@hooks'
import { usePubSub, employeeServices } from '@services'
import { storageHelpers } from '@utils'
import { customToast, ROUTES, CONNECTION_STATUS } from '@common'
import { findGroupByJoinedName, parseGroupId } from '@common'
import WebsocketReconnect from '../TestComponent/WebsocketReconnect'

const SettingsModal = ({ isOpen, toggle }) => {
    const navigate = useNavigate()
    const { cafeGroups } = useDashboardRedux()
    const { groups, leaveGroup, connectionStatus, disconnect } = usePubSub()

    const joinedGroupName = Array.isArray(groups) && groups.length > 0 ? groups[0] : null
    const isConnected = connectionStatus === CONNECTION_STATUS.CONNECTED && joinedGroupName

    // Extract group name from joinedGroupName
    const displayGroupInfo = useMemo(() => {
        if (!joinedGroupName) {
            return { name: 'Not Connected', id: null }
        }

        const match = findGroupByJoinedName(joinedGroupName, cafeGroups)
        if (match) {
            return { name: match.name, id: match.id }
        }

        // Fallback: return parsed group info
        const { numericId, formattedId } = parseGroupId(joinedGroupName)
        return { name: joinedGroupName, id: numericId || formattedId }
    }, [joinedGroupName, cafeGroups])

    const handleLogout = useCallback(async () => {
        try {
            // Leave current group if connected
            if (joinedGroupName) {
                try {
                    await leaveGroup(joinedGroupName)
                } catch (error) {
                    // Ignore errors when leaving group
                }
            }

            // Hard disconnect: clear desired groups to prevent reconnection
            // This ensures the connection won't automatically reconnect after logout
            disconnect({ clearDesiredGroups: true })

            // Clear employee tokens
            employeeServices.clearEmployeeTokens()

            // Clear stored group name
            storageHelpers.clearCheckoutAssistantCashRegisterId()

            // Show success message
            customToast.success('Logged out successfully', { duration: 2000 })

            // Close modal
            toggle()

            // Navigate to login page
            navigate(ROUTES.CheckoutAssistant.login)
        } catch (error) {
            customToast.error('Failed to logout. Please try again.', { duration: 3000 })
        }
    }, [joinedGroupName, leaveGroup, disconnect, navigate, toggle])

    return (
        <Modal isOpen={isOpen} toggle={toggle} centered keyboard backdrop size="lg">
            <ModalHeader toggle={toggle} tag="h1">
                Settings
            </ModalHeader>
            <ModalBody className="fs-4">
                <div>
                    {/* <p className="mb-3 text-secondary fs-4">Connection Information</p> */}
                    <div className="rounded p-0">
                        <div className="fw-semibold mb-3 fs-4">Current Connection</div>
                        <div className="list-group">
                            <div className="list-group-item d-flex align-items-center justify-content-between py-4">
                                <div className="d-flex align-items-center fs-4">
                                    <span className="fw-semibold me-3">Channel:</span>
                                    <span>{displayGroupInfo.name}</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    {isConnected ?
                                        <span className="badge bg-primary fs-5 py-2 px-3">Connected</span>
                                    :   <span className="badge bg-secondary fs-5 py-2 px-3">Disconnected</span>}
                                </div>
                            </div>
                            {displayGroupInfo.id && (
                                <div className="list-group-item py-3">
                                    <div className="text-muted fs-5">
                                        <small>Group ID: {displayGroupInfo.id}</small>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mt-4">
                            <p className="text-muted fs-5 mb-0">
                                Connection is automatically managed based on your employee account. The system will automatically
                                reconnect when you return.
                            </p>
                        </div>
                        <div className="mt-4 pt-4 border-top">
                            <WebsocketReconnect />
                            <Button
                                color="warning"
                                onClick={handleLogout}
                                className="w-100"
                                style={{
                                    fontSize: '1rem',
                                    padding: '0.75rem',
                                    fontWeight: '500',
                                }}
                            >
                                <i className="bx bx-log-out me-2"></i>
                                Logout
                            </Button>
                        </div>
                    </div>
                </div>
            </ModalBody>
        </Modal>
    )
}

export default SettingsModal
